// Draws the final export (F20): the 3D render, an optional label with a leader line, and the attribution footer.
import { footerLayout, labelLayout, type Point } from './layout';
import { paletteFor, type Background } from './options';

export function compose(opts: {
  render: HTMLCanvasElement;
  background: Background;
  footer: string;
  label?: { text: string; anchor: Point } | null;
}): HTMLCanvasElement {
  const { render } = opts;
  const w = render.width;
  const h = render.height;
  const pal = paletteFor(opts.background);
  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const ctx = out.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(render, 0, 0);
  const f = footerLayout(h);
  const family = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

  if (opts.label) {
    const font = Math.round(f.font * 1.5);
    ctx.font = `600 ${font}px ${family}`;
    const tw = ctx.measureText(opts.label.text).width;
    const l = labelLayout(opts.label.anchor, w, h, tw, font, f.strip);
    ctx.strokeStyle = pal.accent;
    ctx.lineWidth = Math.max(2, font / 10);
    ctx.beginPath();
    ctx.moveTo(l.lineFrom.x, l.lineFrom.y);
    ctx.lineTo(l.lineTo.x, l.lineTo.y);
    ctx.stroke();
    ctx.fillStyle = pal.accent;
    ctx.beginPath();
    ctx.arc(l.lineFrom.x, l.lineFrom.y, ctx.lineWidth * 1.8, 0, Math.PI * 2);
    ctx.fill();
    // A soft halo keeps the label readable over the body and on transparent backgrounds.
    ctx.lineJoin = 'round';
    ctx.lineWidth = font / 4;
    ctx.strokeStyle = pal.bg;
    ctx.strokeText(opts.label.text, l.text.x, l.text.y);
    ctx.fillStyle = pal.ink;
    ctx.fillText(opts.label.text, l.text.x, l.text.y);
  }

  ctx.fillStyle = pal.bg;
  ctx.fillRect(0, f.y, w, f.strip);
  ctx.font = `${f.font}px ${family}`;
  ctx.fillStyle = pal.ink;
  ctx.textBaseline = 'middle';
  let text = opts.footer;
  while (ctx.measureText(text).width > w - f.pad * 2 && text.length > 20) text = text.slice(0, -2);
  ctx.fillText(text === opts.footer ? text : `${text.trimEnd()}…`, f.pad, f.y + f.strip / 2);
  return out;
}

/** PNG-encodes off the idle queue where possible (canvas.toBlob waits for idle time, which a busy page rarely has). */
export async function toPng(canvas: HTMLCanvasElement): Promise<Blob> {
  if (typeof OffscreenCanvas !== 'undefined') {
    const oc = new OffscreenCanvas(canvas.width, canvas.height);
    const ctx = oc.getContext('2d');
    if (ctx) {
      ctx.drawImage(canvas, 0, 0);
      return oc.convertToBlob({ type: 'image/png' });
    }
  }
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('PNG encoding failed'))), 'image/png'));
}
